import produce, { Draft } from 'immer';
import i18n from '@nextroll/ar-i18n';

import { generateInitialMetadata } from 'store/nodeSlice';
import { ComponentPropertyInputConfig } from 'types/propertyGeneration';
import { RecordField } from 'types/roflWorkflowActionProperty';
import { RecordOperationType } from 'types/roflWorkflowActions';
import {
    ActionConfigValues,
    ActionMetadata,
    ActionMetadataProperty,
    ActionMetadataRecordField,
    IWorkflow,
    WORKFLOW_NODE_VALIDATION_ISSUE_CODES,
    WORKFLOW_STATUS,
} from 'types/rofl';

import { getEnvironment } from './urls';

/**
 * Returns the locale time string formatted like this: 18:05 EST
 */
export const formatTimeShort = () => {
    return new Date().toLocaleTimeString(
        [],
        { timeZoneName: 'short', hour12: false, hour: '2-digit', minute: '2-digit' },
    );
};

/**
 * Returns the GMT timezone, e.g. GMT-6 for MST
 */
export const formatGmtTimezone = () => {
    const offset = new Date().getTimezoneOffset() / 60 || 0;
    return `GMT${offset < 0 ? '+' : '-'}${offset}`;
};

// create enum type for setup and edit
export enum WorkflowPathType {
    SETUP = 'setup',
}

export const isAuthError = (err: string) => {
    return err === WORKFLOW_NODE_VALIDATION_ISSUE_CODES.AUTHENTICATION_DISCONNECTED
        || err === WORKFLOW_NODE_VALIDATION_ISSUE_CODES.MISSING_PERMISSIONS;
};

/**
 * Returns whether or not a user has customer role (NOT internal users besides engineers)
 */
export const isRoleUserExternalOrEngineer = (actualUserRole: string | undefined): boolean => {
    if (!actualUserRole) {
        return false;
    }
    const allowedRoles = ['engineer', 'user'];
    return allowedRoles.includes(actualUserRole);
};

/**
 * isNullOrUndefined whether or not a value is nullish
 */
export const isNullOrUndefined = (value: unknown): boolean => {
    if (value === undefined || value === null) {
        return true;
    }
    return false;
};

/**
 * isFieldDefinitionRequired returns whether a field definition is required or not
 */
export const isFieldDefinitionRequired = (
    field: RecordField, recordOperationType: RecordOperationType | undefined)
: boolean => {
    const isCreateOrUpsert = (
        [RecordOperationType.CREATE, RecordOperationType.UPSERT] as (RecordOperationType | undefined)[]
    ).includes(recordOperationType);
    return field.required && isCreateOrUpsert;
};

/**
 * getUpdatedMetadata updates the metadata for a record field and returns it
 * @param inputConfig the input config from the generative componentent
 * @param field The field we're updating in the metadata
 * @param actionMetadataProperty Contains the updated lookup metadata for the desired field to be updated.
 * @param [metadata] - Optional, explicit metadata to update
 */
export const getUpdatedMetadata = (inputConfig: ComponentPropertyInputConfig,
    field: RecordField, actionMetadataProperty: ActionMetadataProperty, metadata?: ActionMetadata) => {
    const originalMetadata = metadata || inputConfig.actionMetadata || generateInitialMetadata();
    const updatedMetadata = produce(originalMetadata, draftMetadata => {
        // properties
        const draftMetadataProperties = draftMetadata.properties;
        draftMetadataProperties[inputConfig.inputProperty.name] ??= {};

        // property
        const draftMetadataProperty = draftMetadataProperties[inputConfig.inputProperty.name];
        draftMetadataProperty.fields ??= {};
        draftMetadataProperty.fields[field.name] = draftMetadataProperty.fields[field.name] ?? {};

        // field
        const draftMetadataPropertyField = draftMetadataProperty.fields[field.name];
        draftMetadataPropertyField.lookup = actionMetadataProperty.lookup;
    });

    return updatedMetadata;
};

/**
 * getFieldMetadata gets the property metadata, creates an empty structure up to the field if it doesn't exist,
 * and allows for modification
 * @param inputConfig - the input config from the generative component
 * @param field - The field we're updating in the metadata
 * @param modifyFieldData - Function that modifies fields metadata using an immer Draft containing all fields as input
 * The mutations done by this function will affect the final return value of getFieldMetadata.
 */
export const getFieldMetadata = (
    inputConfig: ComponentPropertyInputConfig,
    field: RecordField, modifyFieldData: (draft: Draft<{[name: string]: ActionMetadataRecordField}>) => void
) => (
    produce(inputConfig.actionMetadataProperty ?? { fields: {} }, draft => {
        if (!draft.fields?.[field.name]) {
            draft.fields ??= {};
            draft.fields[field.name] = {
                index: -1, // this will be corrected to the right index later by syncActionConfig
            };
        }
        modifyFieldData(draft.fields);
    })
);

/**
 * deleteFieldFromWorkflowConfig deletes a field from the workflowConfig and returns the updated workflowConfig.
 */
export const deleteFieldFromWorkflowConfig = (
    workflowConfig: ActionConfigValues,
    inputPropertyName: string,
    field: RecordField
) => {
    const newWorkflowConfig = produce(workflowConfig, draftWorkflowConfig => {
        if (draftWorkflowConfig[
            inputPropertyName
        ]?.[field.name] !== undefined) {
            delete draftWorkflowConfig[
                inputPropertyName
            ][field.name];
        }
    });
    return newWorkflowConfig;
};

/**
 * deleteFieldFromMetadata deletes a field from the metadata and returns the updated metadata.
 */
export const deleteFieldFromMetadata = (
    metadata: ActionMetadata,
    inputPropertyName: string,
    field: RecordField
) => {
    const newMetadata = produce(metadata, draftMetadata => {
        if (draftMetadata.properties[
            inputPropertyName]?.fields?.[field.name]) {
            delete draftMetadata.properties[
                inputPropertyName]?.fields?.[field.name];
        }
    });
    return newMetadata;
};

/**
 * prints a warning to the console if in development mode
 */
export const consoleWarn = (...messages: string[]) => {
    if (getEnvironment() === 'development') {
        // eslint-disable-next-line no-console
        console.warn(messages);
    }
};

/**
 * determine if the workflow is live, configured to enroll current entities in the first run and it still hasn't run
 */
export const workflowHasNotRun = (workflow: IWorkflow) => {
    return workflow.status === WORKFLOW_STATUS.WORKFLOW_ON
        && workflow.schema?.trigger?.include_current_entities_in_first_run
        && !workflow.last_stopped_at;
};

/**
 * convert the timestamp to the age as 'years', 'days', 'hours', or 'minutes'
 */
const NOW = i18n.moment();
export const formatLastEnrollmentAge = (timestamp?: string) => {
    if (!timestamp) {
        return i18n.gettext('N/A');
    }
    const time = i18n.moment(timestamp);
    const unit = (['years', 'days', 'hours', 'minutes'] as moment.unitOfTime.Diff[]).find(diff => NOW.diff(time, diff) > 0);
    if (!unit) {
        return i18n.gettext('N/A');
    }
    return i18n.gettext('{{ age }} {{ unit }} ago', { age: NOW.diff(time, unit), unit });
};
